import { SxProps, Theme } from "@mui/material";
import { userStatus } from "types/perSeat";
import seatReductionTokens from "./SeatReduction.tokens";

const t = seatReductionTokens;

/**
 * Chrome for the keep-picker, drawn from the Settings palette so the dialog
 * reads as part of the same product as the Team table behind it. The summary
 * and the search stay put while only the rows scroll — the count and the
 * confirm button must never be the thing you have to scroll to find.
 */

export const titleSx: SxProps<Theme> = {
  fontSize: t.font.title,
  fontWeight: t.weight.strong,
  color: t.color.ink,
  lineHeight: "normal",
};

export const introSx: SxProps<Theme> = {
  fontSize: t.font.intro,
  fontWeight: t.weight.regular,
  color: t.color.muted,
  lineHeight: 1.55,
  mb: t.space.sectionGap,
};

/** Progress + consequence strip, pinned above the list. */
export const summarySx: SxProps<Theme> = {
  p: t.space.summaryPadding,
  mb: t.space.sectionGap,
  borderRadius: t.radius.control,
  bgcolor: t.color.headerBg,
  border: `${t.border.hairline} solid ${t.color.headerBorder}`,
};

export const summaryRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: "10px",
  mb: "9px",
};

export const summaryCountSx = (isComplete: boolean): SxProps<Theme> => ({
  fontSize: t.font.summaryCount,
  fontWeight: t.weight.strong,
  color: isComplete ? t.color.success : t.color.ink,
  lineHeight: "normal",
});

export const summaryHintSx: SxProps<Theme> = {
  fontSize: t.font.summaryHint,
  fontWeight: t.weight.regular,
  color: t.color.subtle,
  lineHeight: "normal",
};

export const meterTrackSx: SxProps<Theme> = {
  height: t.size.meterHeight,
  borderRadius: t.radius.pill,
  bgcolor: t.color.meterTrack,
  overflow: "hidden",
};

export const meterFillSx = (
  percent: number,
  isComplete: boolean,
): SxProps<Theme> => ({
  width: `${Math.min(100, Math.max(0, percent))}%`,
  height: t.size.meterHeight,
  borderRadius: t.radius.pill,
  bgcolor: isComplete ? t.color.success : t.color.accent,
  transition: "width 160ms ease",
});

export const searchSx: SxProps<Theme> = {
  width: "100%",
  mb: t.space.sectionGap,
};

/** Scroller holding the rows; sized so the footer stays on screen. */
export const listSx: SxProps<Theme> = {
  maxHeight: t.size.listMaxHeight,
  overflowY: "auto",
  border: `${t.border.hairline} solid ${t.color.cardBorder}`,
  borderRadius: t.radius.card,
  bgcolor: t.color.surface,
};

/**
 * `isLocked` is a row that can't be changed because it is already kept — the
 * picker's own seat. It shares "not interactive" with a disabled row but not
 * the fading: dimming a seat that *is* being kept reads as excluded, which is
 * the opposite of what it means.
 */
export const rowSx = (
  isSelected: boolean,
  isDisabled: boolean,
  isLocked = false,
): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  gap: t.space.nameGap,
  px: t.space.rowX,
  py: t.space.rowY,
  cursor: isLocked ? "default" : isDisabled ? "not-allowed" : "pointer",
  borderBottom: `${t.border.hairline} solid ${t.color.rowDivider}`,
  bgcolor: isSelected ? t.color.selectedBg : "transparent",
  opacity: isDisabled && !isLocked ? 0.45 : 1,
  transition: "background-color 120ms ease",
  "&:last-of-type": { borderBottom: "none" },
  "&:hover": {
    bgcolor:
      isDisabled && !isLocked
        ? "transparent"
        : isSelected
          ? t.color.selectedBg
          : t.color.hoverBg,
  },
  // The row is a <label>; the focus ring belongs on all of it, not just the box.
  "&:focus-within": {
    outline: `2px solid ${t.color.accent}`,
    outlineOffset: "-2px",
  },
});

export const checkboxSx: SxProps<Theme> = {
  p: 0,
  flexShrink: 0,
  color: t.color.cardBorder,
  "&.Mui-checked": { color: t.color.checkedFill },
};

/**
 * The picker's own seat: checked and not changeable. MUI greys a disabled box
 * whether or not it is ticked, which would show the one guaranteed seat as the
 * least certain on the list — so it keeps the checked colour.
 */
export const lockedCheckboxSx: SxProps<Theme> = {
  ...checkboxSx,
  "&.Mui-checked": { color: t.color.checkedFill },
  "&.Mui-disabled": { color: t.color.checkedFill },
};

/** Neutral monogram, exactly as the Team table draws it. */
export const avatarSx: SxProps<Theme> = {
  width: t.size.avatar,
  height: t.size.avatar,
  flexShrink: 0,
  borderRadius: t.radius.avatar,
  bgcolor: t.color.avatarBg,
  color: t.color.avatarText,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: t.font.avatar,
  fontWeight: t.weight.strong,
  textTransform: "uppercase",
  lineHeight: "normal",
};

export const nameSx: SxProps<Theme> = {
  fontSize: t.font.name,
  fontWeight: t.weight.medium,
  color: t.color.ink,
  lineHeight: "normal",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const emailSx: SxProps<Theme> = {
  display: "block",
  mt: "2px",
  fontSize: t.font.email,
  fontWeight: t.weight.regular,
  color: t.color.muted,
  lineHeight: "normal",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const statusSx = (status: string): SxProps<Theme> => ({
  flexShrink: 0,
  fontSize: t.font.status,
  fontWeight: t.weight.strong,
  lineHeight: "normal",
  // Keyed off the shared status constants, like the component's own labels —
  // two copies of these strings is one rename away from drifting apart.
  color:
    status === userStatus.pendingVerification
      ? t.color.statusInvited
      : status === userStatus.active
        ? t.color.statusActive
        : t.color.statusInactive,
});

export const emptyStateSx: SxProps<Theme> = {
  py: t.space.emptyY,
  px: t.space.rowX,
  textAlign: "center",
  fontSize: t.font.empty,
  color: t.color.muted,
};
